import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  FileCheck, ArrowLeft, Download, Eye, Check, X, Clock, 
  User, BookOpen, Calendar, Send, FileText, Filter,
  CheckCircle2, AlertTriangle, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';

const GradingPage = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showGradingDialog, setShowGradingDialog] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    course: 'all',
    status: 'all',
    search: ''
  });

  // Grading form
  const [gradingData, setGradingData] = useState({
    score: '',
    feedback: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [submissionsRes, coursesRes] = await Promise.all([
        api.get('/trainer/submissions-to-grade'),
        api.get('/courses')
      ]);
      setSubmissions(submissionsRes.data || []);
      setCourses(coursesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const openGradingDialog = (submission) => {
    setSelectedSubmission(submission);
    setGradingData({ score: '', feedback: '' });
    setShowGradingDialog(true);
  };

  const handleGrade = async () => {
    if (!gradingData.score) {
      toast.error('Veuillez entrer une note');
      return;
    }

    const score = parseFloat(gradingData.score);
    if (isNaN(score) || score < 0 || score > 20) {
      toast.error('La note doit être comprise entre 0 et 20');
      return;
    }

    try {
      await api.put(`/homework-submissions/${selectedSubmission.id}/grade`, {
        score: score * 5, // Convert /20 to /100
        feedback: gradingData.feedback
      });

      toast.success('Devoir corrigé ! L\'étudiant a été notifié.');
      setShowGradingDialog(false);
      fetchData(); // Refresh list
    } catch (error) {
      console.error('Error grading:', error);
      toast.error('Erreur lors de la correction');
    }
  };

  // Filter submissions
  const filteredSubmissions = submissions.filter(sub => {
    if (filters.course !== 'all' && sub.course_name !== filters.course) return false;
    if (filters.status === 'pending' && sub.status !== 'submitted') return false;
    if (filters.status === 'graded' && sub.status !== 'graded') return false;
    if (filters.search && !sub.student_name.toLowerCase().includes(filters.search.toLowerCase()) &&
        !sub.homework_title.toLowerCase().includes(filters.search.toLowerCase())) return false;
    return true;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'submitted':
        return <Badge className="bg-amber-100 text-amber-700">À corriger</Badge>;
      case 'graded':
        return <Badge className="bg-emerald-100 text-emerald-700">Corrigé</Badge>;
      case 'late':
        return <Badge className="bg-red-100 text-red-700">En retard</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-700">{status}</Badge>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non spécifié';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const pendingCount = submissions.filter(s => s.status === 'submitted').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header title="Corrections" onMenuClick={onMenuClick} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          className="mb-6"
          onClick={() => navigate('/dashboard')}
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour au tableau de bord
        </Button>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-700">{pendingCount}</p>
                <p className="text-sm text-amber-600">À corriger</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {submissions.filter(s => s.status === 'graded').length}
                </p>
                <p className="text-sm text-slate-600 dark:text-slate-400">Corrigés</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                <FileText className="w-6 h-6 text-violet-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{submissions.length}</p>
                <p className="text-sm text-slate-600 dark:text-slate-400">Total soumissions</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Filtres:</span>
              </div>
              
              <Select 
                value={filters.course} 
                onValueChange={(v) => setFilters(prev => ({ ...prev, course: v }))}
              >
                <SelectTrigger className="w-48" data-testid="filter-course">
                  <SelectValue placeholder="Tous les cours" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les cours</SelectItem>
                  {[...new Set(submissions.map(s => s.course_name))].map(course => (
                    <SelectItem key={course} value={course}>{course}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select 
                value={filters.status} 
                onValueChange={(v) => setFilters(prev => ({ ...prev, status: v }))}
              >
                <SelectTrigger className="w-40" data-testid="filter-status">
                  <SelectValue placeholder="Tous les statuts" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="pending">À corriger</SelectItem>
                  <SelectItem value="graded">Corrigés</SelectItem>
                </SelectContent>
              </Select>
              
              <Input
                placeholder="Rechercher..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="w-64"
                data-testid="filter-search"
              />
            </div>
          </CardContent>
        </Card>

        {/* Submissions List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-violet-600" />
              Devoirs à corriger ({filteredSubmissions.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredSubmissions.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                  Aucun devoir à corriger
                </h3>
                <p className="text-slate-600 dark:text-slate-400">
                  Toutes les soumissions ont été corrigées ou aucune ne correspond aux filtres.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-200 dark:divide-slate-700">
                {filteredSubmissions.map((submission) => (
                  <div 
                    key={submission.id}
                    className="py-4 flex items-center gap-4 hover:bg-slate-50 dark:hover:bg-slate-800 -mx-4 px-4 transition-colors"
                    data-testid={`submission-${submission.id}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-violet-600" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-slate-900 dark:text-white truncate">
                          {submission.student_name}
                        </h4>
                        {getStatusBadge(submission.status)}
                      </div>
                      <p className="text-sm text-violet-600 font-medium truncate">
                        {submission.homework_title}
                      </p>
                      <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <BookOpen className="w-3 h-3" />
                          {submission.course_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(submission.submitted_at)}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {submission.file_url && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => window.open(submission.file_url, '_blank')}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      )}
                      
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => navigate(`/trainer/students/${submission.student_id}`)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      
                      {submission.status === 'submitted' && (
                        <Button 
                          size="sm"
                          className="bg-violet-600 hover:bg-violet-700"
                          onClick={() => openGradingDialog(submission)}
                          data-testid={`grade-btn-${submission.id}`}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Corriger
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Grading Dialog */}
      <Dialog open={showGradingDialog} onOpenChange={setShowGradingDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-violet-600" />
              Corriger le devoir
            </DialogTitle>
          </DialogHeader>
          
          {selectedSubmission && (
            <div className="space-y-4 py-4">
              {/* Student Info */}
              <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800">
                <div className="flex items-center gap-3 mb-2">
                  <User className="w-5 h-5 text-violet-600" />
                  <span className="font-medium text-slate-900 dark:text-white">
                    {selectedSubmission.student_name}
                  </span>
                </div>
                <p className="text-sm text-violet-600 font-medium mb-1">
                  {selectedSubmission.homework_title}
                </p>
                <p className="text-xs text-slate-500">
                  Soumis le {formatDate(selectedSubmission.submitted_at)}
                </p>
              </div>

              {/* Submission Content */}
              <div>
                <label className="text-sm font-medium mb-2 block">Contenu soumis</label>
                <div className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 max-h-48 overflow-y-auto">
                  <p className="text-sm text-slate-600 dark:text-slate-400 whitespace-pre-wrap">
                    {selectedSubmission.content || 'Aucun texte - voir fichier joint'}
                  </p>
                </div>
                {selectedSubmission.file_url && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-2"
                    onClick={() => window.open(selectedSubmission.file_url, '_blank')}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Télécharger le fichier
                  </Button>
                )}
              </div>

              {/* Grading */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Note sur 20 *</label>
                  <Input
                    type="number"
                    min="0"
                    max="20"
                    step="0.5"
                    value={gradingData.score}
                    onChange={(e) => setGradingData(prev => ({ ...prev, score: e.target.value }))}
                    placeholder="Ex: 15"
                    data-testid="input-grade"
                  />
                </div>
                <div className="flex items-end">
                  <div className="flex gap-2">
                    {[10, 12, 14, 16, 18, 20].map(note => (
                      <Button
                        key={note}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setGradingData(prev => ({ ...prev, score: note.toString() }))}
                        className={gradingData.score === note.toString() ? 'bg-violet-100 border-violet-300' : ''}
                      >
                        {note}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Commentaire / Feedback</label>
                <Textarea
                  value={gradingData.feedback}
                  onChange={(e) => setGradingData(prev => ({ ...prev, feedback: e.target.value }))}
                  placeholder="Vos commentaires sur le travail de l'étudiant..."
                  rows={4}
                  data-testid="input-feedback"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGradingDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleGrade}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="submit-grade"
            >
              <Send className="w-4 h-4 mr-2" />
              Valider la correction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GradingPage;
